import { ReactNode, useRef, useState } from 'react';
import { Box, BoxProps, BreadcrumbLinkProps, Divider } from '@bitrise/bitkit';
import SubHeader, { SubHeaderProps } from '@/components/SubHeader/SubHeader';
import { HEADER_HEIGHT, NAV_WIDTH } from '@/constants';
import InternalFooter from '@/components/InternalFooter/InternalFooter';
import PageLayoutSidebar, { PageLayoutSidebarProps } from './PageLayoutSidebar';

export type PageLayoutProps = {
  children: ReactNode;
  footerBackground?: BoxProps['bg'];
  header: Omit<SubHeaderProps, 'children'> & {
    children?: (props: { isSticky: boolean }) => ReactNode;
    colorScheme?: 'gray' | 'white';
    extraContent?: ReactNode;
    pageTitleString: string;
    showDivider?: boolean;
  };
  isMobile: boolean;
  onSidebarOpen?(isOpen: boolean): void;
  platformShell?: {
    headerHeight: number;
    navWidth: number;
  };
  sidebar?: Omit<PageLayoutSidebarProps, 'isMobile' | 'platformShell' | 'width' | 'isStickyHeader' | 'subHeaderRef'> & {
    width?: PageLayoutSidebarProps['width'];
  };
};

const PageLayout = ({
  children,
  footerBackground,
  header,
  isMobile,
  onSidebarOpen,
  platformShell = {
    headerHeight: HEADER_HEIGHT,
    navWidth: NAV_WIDTH,
  },
  sidebar,
}: PageLayoutProps) => {
  const { colorScheme, children: headerChildren, showDivider: showHeaderDivider = true, ...headerRest } = header;

  const subHeaderRef = useRef<HTMLDivElement>(null);

  const [isStickyHeader, setIsStickyHeader] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  let mobileBreadCrumbdata: BreadcrumbLinkProps[];

  if (!sidebar || isSidebarOpen) {
    mobileBreadCrumbdata = [header.breadcrumbData?.[header.breadcrumbData.length - 1] as BreadcrumbLinkProps];
  } else {
    mobileBreadCrumbdata = [
      {
        as: 'button' as BreadcrumbLinkProps['as'],
        key: '1',
        children: header.pageTitleString,
        onClick: () => {
          setIsSidebarOpen(true);
          onSidebarOpen?.(true);
        },
      },
    ];
  }

  const pageLayoutSidebarWidth = sidebar?.width ? sidebar.width : '16rem';

  return (
    <>
      <Box
        data-testid="page-layout-header"
        background={colorScheme === 'gray' ? 'neutral.100' : undefined}
        paddingInline={isMobile ? '12' : '32'}
        ref={subHeaderRef}
      >
        <SubHeader
          {...headerRest}
          breadcrumbData={isMobile ? mobileBreadCrumbdata : header.breadcrumbData}
          shouldSticky={!isMobile}
          onStickyChange={setIsStickyHeader}
        >
          {headerChildren?.({ isSticky: isStickyHeader })}
        </SubHeader>
        {header.extraContent}
      </Box>
      {showHeaderDivider && <Divider />}
      <Box flex="1" display="flex" flexDirection={isMobile ? 'column' : 'row'}>
        {sidebar && (isSidebarOpen || !isMobile) && (
          <PageLayoutSidebar
            {...sidebar}
            isStickyHeader={isStickyHeader}
            width={pageLayoutSidebarWidth}
            isMobile={isMobile}
            platformShell={platformShell}
            subHeaderRef={subHeaderRef}
          />
        )}
        <Box
          display="flex"
          flex="1"
          flexDir="column"
          maxW={['unset', sidebar ? `calc(100% - ${pageLayoutSidebarWidth})` : 'unset']}
        >
          {(!isMobile || !isSidebarOpen) && <Box flex="1">{children}</Box>}
          <InternalFooter
            bg={colorScheme === 'gray' ? 'neutral.100' : footerBackground}
            marginTop="auto"
            paddingLeft="12"
          />
        </Box>
      </Box>
    </>
  );
};

export default PageLayout;
